import type { Organization, PaginatedList, Patrol } from '@local/common-lib';
import {
  Card,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import {
  CenterCard,
  CenterLayout,
} from '../../components/page-layout/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useHistoryState } from '../../hooks/use-history-state.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { ErrorPage } from '../error/index.js';
import { NarrowPatrolList } from './narrow-view.js';
import { NoPastPatrols } from './no-patrols.js';
import { PatrolsSummary } from './summary.js';
import { WidePatrolList } from './wide-view.js';

export const PastPatrolListPage: FC = () => {
  useSetTitle('Past Patrols');
  const auth = useAuthenticated();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [pagination, setPagination] = useHistoryState('pagination', {
    limit: 12,
    skip: 0,
  });

  const [organization] = useAxios<Organization.Type>('/api/v1/my/organization');

  const [patrols] = useAxios<PaginatedList<Patrol.Type>>(
    '/api/v1/patrols',
    useMemo(
      () => ({
        params: {
          ...pagination,
          filter: JSON.stringify({
            deleted: null,
            when: { $lte: DateTime.now() },
          }),
          sort: '-when',
        },
      }),
      [pagination],
    ),
  );

  const hideUnstartedBadge =
    organization.data?.hideUnstartedPatrolsBadge ?? false;

  if (organization.loading || patrols.loading) {
    return (
      <Stack spacing={3}>
        {pagination.skip === 0 && (
          <CenterCard>{/* <PatrolsSummary /> */}</CenterCard>
        )}

        <MiddleSpinner />
      </Stack>
    );
  }

  if (patrols.error) {
    return <ErrorPage error={patrols.error} />;
  }

  if (!patrols.data.matches) {
    return <NoPastPatrols />;
  }

  const limited = !auth.userInfo.hasRole(
    'admin',
    'watcher',
    'summary-recipient',
  );
  const handleSelection = (id: string): unknown => navigate(`/patrol/${id}`);

  return (
    <CenterLayout>
      <Stack spacing={3}>
        {pagination.skip === 0 && (
          <Card>
            <PatrolsSummary />
          </Card>
        )}

        <Card>
          {smallScreen ? (
            <NarrowPatrolList
              handleSelection={!limited ? handleSelection : undefined}
              hideUnstartedBadge={hideUnstartedBadge}
              hideWho={false}
              patrols={patrols.data}
            />
          ) : (
            <WidePatrolList
              handleSelection={!limited ? handleSelection : undefined}
              hideUnstartedBadge={hideUnstartedBadge}
              hideWho={false}
              patrols={patrols.data}
            />
          )}
        </Card>

        {patrols.data.matches > pagination.limit ? (
          <Pagination
            count={Math.ceil(patrols.data.matches / pagination.limit)}
            onChange={(_, page) => {
              setPagination({
                limit: pagination.limit,
                skip: pagination.limit * (page - 1),
              });
            }}
            page={Math.floor(pagination.skip / pagination.limit) + 1}
            showFirstButton
            showLastButton
            sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
          />
        ) : undefined}
      </Stack>
    </CenterLayout>
  );
};
